import {
  createContext,
  createSignal,
  onCleanup,
  onMount,
  useContext,
  type Accessor,
  type ParentProps,
} from 'solid-js';
import type { ProductRole } from '@academy/domain';
import type { BootstrapData } from '@academy/machines';
import { environmentStatus, getSupabase } from '../services/supabase';
import { clearQueryCacheForAccount } from '../services/query-cache';
import {
  hydrateAllowlistedQuerySnapshots,
  purgeAllQuerySnapshots,
  purgeQuerySnapshotsForAccount,
} from '../services/query-snapshot-storage';
import { clearWorkspacePrefetchRecordsForAccount } from '../services/workspace-prefetch';

type SessionContextValue = Omit<BootstrapData, 'roles'> & {
  readonly roles: readonly ProductRole[];
  activeRole: Accessor<ProductRole | null>;
  capabilityStatus: Accessor<'ready' | 'invalidated'>;
  lastCapabilityCheckAt: Accessor<number>;
  setActiveRole: (role: ProductRole) => void;
  refreshCapabilities: (reason?: 'routine' | 'invalidation') => Promise<readonly ProductRole[]>;
};

const SessionContext = createContext<SessionContextValue>();

export const capabilityInvalidationEvent = 'academy:capability-invalidated';
export const capabilityFreshnessMs = 30_000;

export const isAuthoritativeCapabilityError = (error: unknown) => {
  if (typeof error !== 'object' || error === null) return false;
  const status: unknown = Reflect.get(error, 'status') ?? Reflect.get(error, 'statusCode');
  const code: unknown = Reflect.get(error, 'code');
  return (
    status === 401 ||
    status === 403 ||
    code === 'PGRST301' ||
    code === 'JWT_EXPIRED' ||
    code === 'BAD_JWT'
  );
};

export const SessionProvider = (
  props: ParentProps<{
    value: BootstrapData;
    refreshCapabilities?: () => Promise<BootstrapData | null>;
    listenForInvalidations?: boolean;
  }>,
) => {
  const initialActiveRole = props.value.roles[0] ?? null;
  hydrateAllowlistedQuerySnapshots({
    accountId: props.value.accountId,
    activeRole: initialActiveRole,
    roles: props.value.roles,
    sessionSubjectHash: props.value.sessionSubjectHash,
    sessionGenerationHash: props.value.sessionGenerationHash,
  });
  const [roles, setRoles] = createSignal<readonly ProductRole[]>(props.value.roles);
  const [activeRole, setActiveRoleValue] = createSignal<ProductRole | null>(initialActiveRole);
  const [capabilityStatus, setCapabilityStatus] = createSignal<'ready' | 'invalidated'>('ready');
  const [lastCapabilityCheckAt, setLastCapabilityCheckAt] = createSignal(Date.now());
  const setActiveRole = (role: ProductRole) => {
    if (roles().includes(role) && role !== activeRole()) {
      clearWorkspacePrefetchRecordsForAccount(props.value.accountId);
      clearQueryCacheForAccount(props.value.accountId);
      purgeQuerySnapshotsForAccount(props.value.accountId);
      setActiveRoleValue(role);
    }
  };
  let refreshInFlight: Promise<readonly ProductRole[]> | null = null;
  let invalidationVersion = 0;
  let reconciledInvalidationVersion = 0;
  const refreshCapabilities = (
    reason: 'routine' | 'invalidation' = 'routine',
  ): Promise<readonly ProductRole[]> => {
    if (reason === 'invalidation') {
      clearWorkspacePrefetchRecordsForAccount(props.value.accountId);
      clearQueryCacheForAccount(props.value.accountId);
      purgeQuerySnapshotsForAccount(props.value.accountId);
      invalidationVersion += 1;
      setCapabilityStatus('invalidated');
    }
    if (refreshInFlight) {
      return refreshInFlight.then(() =>
        reconciledInvalidationVersion < invalidationVersion
          ? refreshCapabilities('routine')
          : roles(),
      );
    }
    const refreshInvalidationVersion = invalidationVersion;
    refreshInFlight = (async () => {
      try {
        const refreshed = props.refreshCapabilities
          ? await props.refreshCapabilities()
          : props.value;
        const nextRoles =
          refreshed?.accountId === props.value.accountId ? refreshed.roles : ([] as const);
        if (refreshed && refreshed.accountId !== props.value.accountId) purgeAllQuerySnapshots();
        const removedAuthority = roles().some((role) => !nextRoles.includes(role));
        if (removedAuthority) {
          clearWorkspacePrefetchRecordsForAccount(props.value.accountId);
          clearQueryCacheForAccount(props.value.accountId);
          purgeQuerySnapshotsForAccount(props.value.accountId);
        }
        setRoles(nextRoles);
        const selectedRole = activeRole();
        if (selectedRole === null || !nextRoles.includes(selectedRole)) {
          setActiveRoleValue(nextRoles[0] ?? null);
        }
        setLastCapabilityCheckAt(Date.now());
        reconciledInvalidationVersion = refreshInvalidationVersion;
        if (reconciledInvalidationVersion >= invalidationVersion) {
          setCapabilityStatus('ready');
        }
        return nextRoles;
      } catch (error) {
        if (isAuthoritativeCapabilityError(error)) {
          clearWorkspacePrefetchRecordsForAccount(props.value.accountId);
          clearQueryCacheForAccount(props.value.accountId);
          purgeQuerySnapshotsForAccount(props.value.accountId);
          setRoles([]);
          setActiveRoleValue(null);
          setLastCapabilityCheckAt(Date.now());
          reconciledInvalidationVersion = refreshInvalidationVersion;
          if (reconciledInvalidationVersion >= invalidationVersion) {
            setCapabilityStatus('ready');
          }
        }
        throw error;
      }
    })().finally(() => {
      refreshInFlight = null;
    });
    return refreshInFlight;
  };
  const value: SessionContextValue = {
    ...props.value,
    get roles() {
      return roles();
    },
    activeRole,
    capabilityStatus,
    lastCapabilityCheckAt,
    setActiveRole,
    refreshCapabilities,
  };

  onMount(() => {
    if (
      !props.refreshCapabilities ||
      !props.listenForInvalidations ||
      !environmentStatus().configured
    ) {
      return;
    }
    const client = getSupabase();
    const mountedAt = new Date().toISOString();
    const seenInvalidations = new Set<string>();
    const refreshAfterInvalidation = () => {
      window.dispatchEvent(new Event(capabilityInvalidationEvent));
      return refreshCapabilities('invalidation');
    };
    const pollInvalidations = async () => {
      const { data, error } = await client
        .from('capability_invalidations_ui_v1' as 'capability_invalidation')
        .select('capability_invalidation_id')
        .gt('created_at', mountedAt)
        .order('created_at', { ascending: true })
        .limit(20);
      if (error) return;
      const unseen = (data ?? []).filter(
        (item) => !seenInvalidations.has(item.capability_invalidation_id),
      );
      if (unseen.length === 0) return;
      unseen.forEach((item) => seenInvalidations.add(item.capability_invalidation_id));
      await refreshAfterInvalidation();
    };
    const channel = client
      .channel(`capability-invalidation:${props.value.accountId}`)
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'capability_invalidation',
          filter: `account_id=eq.${props.value.accountId}`,
        },
        () => void refreshAfterInvalidation(),
      )
      .subscribe();
    const pollTimer = window.setInterval(() => void pollInvalidations(), 2_000);
    void pollInvalidations();
    const refreshIfStale = () => {
      if (Date.now() - lastCapabilityCheckAt() >= capabilityFreshnessMs) {
        void refreshCapabilities();
      }
    };
    const refreshOnFocus = () => refreshIfStale();
    const refreshWhenVisible = () => {
      if (document.visibilityState === 'visible') refreshIfStale();
    };
    window.addEventListener('focus', refreshOnFocus);
    document.addEventListener('visibilitychange', refreshWhenVisible);
    onCleanup(() => {
      window.removeEventListener('focus', refreshOnFocus);
      document.removeEventListener('visibilitychange', refreshWhenVisible);
      window.clearInterval(pollTimer);
      void client.removeChannel(channel);
    });
  });

  return <SessionContext.Provider value={value}>{props.children}</SessionContext.Provider>;
};

export const useSession = () => {
  const session = useContext(SessionContext);
  if (!session) throw new Error('Session context is unavailable.');
  return session;
};

export const useOptionalSession = () => useContext(SessionContext);
